import * as React from "react";
import { cva } from "class-variance-authority";
import { Check, Loader2, X } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { buttonVariants, type ButtonProps } from "./button.js";
import { useConfirm, type ConfirmRequest } from "./confirm-dialog.js";

export type AsyncButtonStatus = "idle" | "loading" | "success" | "error";

export interface AsyncButtonProps extends Omit<ButtonProps, "onClick" | "asChild"> {
  /** Uncontrolled mode: if this returns a promise, its settling drives the
   * built-in loading -> done/error animation. If it returns nothing (open a
   * dialog, navigate), the button just acts like a normal button. When
   * `status` is provided, it's fired as a plain notification instead. */
  onClick?: () => Promise<unknown> | void;
  /** Asks first: the click opens the ConfirmationCard dialog stating, in one
   * short line, what the action will do. The button stays idle while the card
   * is open and only starts its pending animation once the user confirms --
   * Cancel leaves it exactly as it was. Every button whose click hits the API
   * should carry one. */
  confirm?: ConfirmRequest;
  /** Controlled mode: when provided, this prop -- not onClick's promise --
   * drives the visual state. Pair with `progress` for a real progress bar,
   * or omit `progress` for an indeterminate loading animation. */
  status?: AsyncButtonStatus;
  /** 0-100. Only meaningful when status === "loading". Changes are
   * smoothly interpolated, never an instant jump. */
  progress?: number;
  loadingText?: React.ReactNode;
  doneLabel?: React.ReactNode;
  doneIcon?: React.ReactNode;
  /** ms the done/error label + icon stay visible before reverting to idle. */
  doneDuration?: number;
  errorLabel?: React.ReactNode;
  /**
   * false renders a plain button: the button's normal variant color at
   * rest, a spinner while pending, and a brief background + label swap on
   * completion/error -- no liquid-fill animation. Use for lower-emphasis
   * actions across the app where the full effect would be too heavy.
   */
  animated?: boolean;
  /** false skips the outward ripple on completion. */
  shockwave?: boolean;
  /** Idle background behind the liquid fill (animated mode only).
   * "variant" (default) rests in the variant's own color and fills with a
   * deeper shade of it -- matches the app's existing buttons. The others
   * rest on a neutral tone. For a one-off color, pass it via className
   * instead, e.g. className="bg-[#1e1e2e] hover:bg-[#2a2a3c]". */
  background?: "variant" | "charcoal" | "slate" | "light";
  /** Corner rounding -- the fill and shockwave ripple follow it. */
  radius?: "sm" | "md" | "lg" | "xl" | "full";
  /** Permanent 1px border: "subtle" follows the text color, "accent" uses
   * the variant's own color. */
  border?: "none" | "subtle" | "accent";
  /** 1px outline shown only on hover. Drawn as an outline, so it never
   * changes the button's size. */
  hoverBorder?: "none" | "subtle" | "accent";
  /** Overrides the liquid fill + shockwave color, e.g.
   * fillClassName="bg-[#7c3aed]". Defaults to the variant's color. */
  fillClassName?: string;
  /** ms -- how quickly the indeterminate fill approaches its cap while
   * pending (a slower pace feels like a "heavier" operation). Only affects
   * indeterminate loading (no numeric `progress`). */
  progressPace?: number;
  /** ms -- how long the snap-to-full-color (success) and snap-to-empty
   * (error/reset) transitions take. */
  transitionDuration?: number;
  /** How far the outward shockwave ripple reaches on completion. "none"
   * disables it regardless of `shockwave`. The button itself never changes
   * size. */
  completionIntensity?: "none" | "subtle" | "normal" | "strong";
}

/**
 * Every default AsyncButton falls back to when a prop is omitted -- a bare
 * `<AsyncButton onClick={save}>Save</AsyncButton>` behaves normally with
 * just these. Pass a prop at the call site only when you want something
 * different.
 */
export const asyncButtonDefaults = {
  size: "default",
  radius: "md",
  border: "none",
  hoverBorder: "accent",
  background: "variant",
  animated: true,
  shockwave: true,
  completionIntensity: "normal",
  /** ms -- matches the reference's ~2s fill pacing */
  progressPace: 2000,
  /** ms -- matches the reference's reset/snap timing */
  transitionDuration: 300,
  /** ms the done/error state stays before reverting to idle */
  doneDuration: 1000,
} as const;

type Phase = "idle" | "pending" | "done" | "error";

const statusToPhase = (s: AsyncButtonStatus): Phase =>
  s === "loading" ? "pending" : s === "success" ? "done" : s === "error" ? "error" : "idle";

const isThenable = (v: unknown): v is PromiseLike<unknown> =>
  typeof v === "object" && v !== null && typeof (v as { then?: unknown }).then === "function";

// One accent color per variant, exposed as --ab-accent so the fill, progress
// glow (tokens.css), border, hover outline and shockwave all share a single
// source instead of each keeping its own per-variant color map. Uses the
// design tokens (not fixed hex) so it follows dark mode.
const accentVariants = cva("", {
  variants: {
    variant: {
      default: "[--ab-accent:var(--color-brand-600)]",
      outline: "[--ab-accent:var(--color-brand-600)]",
      ghost: "[--ab-accent:var(--color-brand-600)]",
      secondary: "[--ab-accent:var(--color-brand-600)]",
      // Same tokens the app's existing buttons use, so a migrated button
      // looks identical at rest.
      success: "[--ab-accent:var(--color-success)]",
      warning: "[--ab-accent:var(--color-warning)]",
      destructive: "[--ab-accent:var(--color-error)]",
      info: "[--ab-accent:var(--color-info-accent)]",
    },
    background: { variant: "", charcoal: "", slate: "", light: "" },
  },
  // On a neutral resting background the fill is the only color on the
  // button, so it uses the vivid accents (and the reference spec's red).
  compoundVariants: [
    { background: ["charcoal", "slate", "light"], variant: "success", class: "[--ab-accent:var(--color-success-accent)]" },
    { background: ["charcoal", "slate", "light"], variant: "warning", class: "[--ab-accent:var(--color-warning-accent)]" },
    { background: ["charcoal", "slate", "light"], variant: "destructive", class: "[--ab-accent:#ff2727]" },
  ],
  defaultVariants: { variant: "default", background: "variant" },
});

// Shockwave peak scale/opacity, consumed by the .async-btn-shockwave
// keyframe in tokens.css.
const intensityVariants = cva("", {
  variants: {
    completionIntensity: {
      none: "",
      subtle: "[--ab-shock-peak:1.25] [--ab-shock-opacity:0.35]",
      normal: "[--ab-shock-peak:1.6] [--ab-shock-opacity:0.5]",
      strong: "[--ab-shock-peak:1.9] [--ab-shock-opacity:0.6]",
    },
  },
  defaultVariants: { completionIntensity: "normal" },
});

const backgroundVariants = cva("", {
  variants: {
    background: {
      // Rests in the variant's own color. --ab-deepen tones down the
      // progressive darkening, since the fill is already a deeper shade.
      variant: "bg-[var(--ab-accent)] text-white hover:bg-[var(--ab-accent)]/90 [--ab-deepen:0.2]",
      // The reference's exact dark charcoal.
      charcoal: "bg-[#27272a] text-white hover:bg-[#323238]",
      slate: "bg-[#334155] text-white hover:bg-[#3f4d63]",
      light: "bg-[#f4f4f5] text-[#27272a] hover:bg-[#e4e4e7]",
    },
  },
  defaultVariants: { background: "variant" },
});

const radiusVariants = cva("", {
  variants: {
    radius: {
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl",
      full: "rounded-full",
    },
  },
  defaultVariants: { radius: "md" },
});

const borderVariants = cva("", {
  variants: {
    border: {
      none: "",
      // Follows the text color, so it reads on dark and light backgrounds.
      subtle: "border border-current/10",
      accent: "border border-[var(--ab-accent)]/25",
    },
  },
  defaultVariants: { border: "none" },
});

const hoverBorderVariants = cva("", {
  variants: {
    hoverBorder: {
      none: "",
      subtle: "hover:outline-1 hover:-outline-offset-1 hover:outline-current/5",
      accent: "hover:outline-1 hover:-outline-offset-1 hover:outline-[color:var(--ab-accent)]/10",
    },
  },
  defaultVariants: { hoverBorder: "accent" },
});

// Every label variant is rendered stacked in the same grid cell, with only
// the active one visible -- so the button is always as wide as its widest
// label and never resizes when the text or icon swaps on completion (e.g.
// "Check In" -> "✓ Checked In"). The outgoing label fades out fully before
// the incoming one fades in, so two differently-laid-out labels never
// overlap mid-crossfade (which read as doubled/ghosted text).
const labelSlot = (active: boolean) =>
  cn(
    "col-start-1 row-start-1 inline-flex items-center justify-center gap-2 transition-opacity motion-reduce:transition-none",
    active ? "opacity-100 duration-150 delay-100" : "opacity-0 duration-100"
  );

// Thin gradient patch that rides the fill's moving edge (the "glint" on
// the water's leading edge).
const FILL_GLINT = "bg-no-repeat bg-[length:6px_100%] bg-[linear-gradient(to_right,rgba(255,255,255,0.9),transparent)]";
// On a variant background the liquid is a deeper shade of the resting color.
const VARIANT_FILL = "bg-[color-mix(in_srgb,var(--ab-accent)_75%,black)]";
const ERROR_TONE = "bg-[var(--color-error-accent)]";
// Plain (non-animated) mode's completion/error flash -- a simple
// background + text color swap, no fill/wave/glow.
const SIMPLE_DONE_TONE = "bg-[var(--color-success-bg)] text-[var(--color-success-fg-strong)]";
const SIMPLE_ERROR_TONE = "bg-[var(--color-error-bg)] text-[var(--color-error-fg-strong)]";

const P_CAP = 0.94; // indeterminate progress asymptotically approaches this, never reaches it
const PROGRESS_MS = 300; // matches the reference's suggested "transition: width 300ms ease-out"
const EASE_OUT = (t: number) => 1 - Math.pow(1 - t, 3);

// Wavy leading edge -- a handful of vertical sample points whose x position
// wobbles around the current progress via a traveling sine wave, expressed
// as percentages so no pixel measurement of the button is needed. Only used
// while pending -- the completed/error state always renders as a flat,
// fully solid fill (`[clip-path:none]`), never a frozen mid-wobble edge.
const WAVE_POINTS = 6;
const WAVE_AMPLITUDE = 3; // percent
const WAVE_SPEED = 0.0035; // radians per ms

function wavyClipPath(p: number, nowMs: number): string {
  // At p === 0, skip the wobble -- otherwise the amplitude alone can leave a
  // thin sliver of color frozen at the left edge once the rAF loop stops.
  if (p <= 0) return "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)";
  const x = p * 100;
  const points = ["0% 0%"];
  for (let i = 0; i <= WAVE_POINTS; i++) {
    const y = (i / WAVE_POINTS) * 100;
    const wobble = WAVE_AMPLITUDE * Math.sin(nowMs * WAVE_SPEED + i * 1.4);
    const xi = Math.min(100, Math.max(0, x + wobble));
    points.push(`${xi.toFixed(2)}% ${y.toFixed(2)}%`);
  }
  points.push("0% 100%");
  return `polygon(${points.join(", ")})`;
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const AsyncButton = React.forwardRef<HTMLButtonElement, AsyncButtonProps>(
  (
    {
      className, variant, size = asyncButtonDefaults.size, children, onClick,
      status, progress, confirm: confirmRequest,
      loadingText, doneLabel, doneIcon, errorLabel,
      doneDuration = asyncButtonDefaults.doneDuration,
      animated = asyncButtonDefaults.animated,
      shockwave = asyncButtonDefaults.shockwave,
      progressPace = asyncButtonDefaults.progressPace,
      transitionDuration = asyncButtonDefaults.transitionDuration,
      completionIntensity = asyncButtonDefaults.completionIntensity,
      background = asyncButtonDefaults.background,
      radius = asyncButtonDefaults.radius,
      border = asyncButtonDefaults.border,
      hoverBorder = asyncButtonDefaults.hoverBorder,
      fillClassName,
      disabled, ...props
    },
    ref
  ) => {
    const requestConfirm = useConfirm();
    const controlled = status !== undefined;
    const [phase, setPhase] = React.useState<Phase>("idle");
    const effectivePhase: Phase = status !== undefined ? statusToPhase(status) : phase;
    const [shockwaveKey, setShockwaveKey] = React.useState(0);
    const mounted = React.useRef(true);
    const buttonRef = React.useRef<HTMLButtonElement | null>(null);
    const motion = React.useRef({ raf: 0, p: 0 });
    const revertTimer = React.useRef(0);
    const prevPhase = React.useRef<Phase>("idle");

    React.useEffect(() => {
      // Reset on setup, not just at useRef() initialization -- React
      // StrictMode double-invokes this effect in dev (mount -> cleanup ->
      // mount again), and without this the component stays "unmounted" from
      // its own perspective after that synthetic cleanup, so pending could
      // never reach done/error.
      mounted.current = true;
      return () => {
        mounted.current = false;
        cancelAnimationFrame(motion.current.raf);
        clearTimeout(revertTimer.current);
      };
    }, []);

    // Writes the live fill progress + wavy clip-path straight onto the DOM
    // node every frame instead of through React state -- it's an
    // indeterminate, asymptotically-slowing value with no fixed end time
    // (and a continuously traveling wave on top of it), so neither can be
    // expressed as a static Tailwind class or a fixed-duration @keyframes
    // block (see the "JS-driven continuous animation values" exception in
    // CLAUDE.md). Only called when animated.
    const setProgress = (p: number, nowMs: number) => {
      motion.current.p = p;
      const node = buttonRef.current;
      if (!node) return;
      node.style.setProperty("--ab-p", p.toFixed(4));
      node.style.setProperty("--ab-clip", wavyClipPath(p, nowMs));
    };

    const stopRaf = () => {
      cancelAnimationFrame(motion.current.raf);
      motion.current.raf = 0;
    };

    const startIndeterminate = () => {
      stopRaf();
      const start = performance.now();
      const from = motion.current.p;
      const step = (now: number) => {
        const eased = 1 - Math.exp(-(now - start) / progressPace);
        setProgress(from + (P_CAP - from) * eased, now);
        motion.current.raf = requestAnimationFrame(step);
      };
      motion.current.raf = requestAnimationFrame(step);
    };

    const driveTo = (to: number, duration: number, onDone?: () => void) => {
      stopRaf();
      const start = performance.now();
      const from = motion.current.p;
      const step = (now: number) => {
        const t = duration > 0 ? Math.min(1, (now - start) / duration) : 1;
        setProgress(from + (to - from) * EASE_OUT(t), now);
        if (t < 1) {
          motion.current.raf = requestAnimationFrame(step);
        } else {
          motion.current.raf = 0;
          onDone?.();
        }
      };
      motion.current.raf = requestAnimationFrame(step);
    };

    // Controlled mode: --ab-p/--ab-clip are driven by the status/progress
    // props instead of an internal click handler. Progress changes are
    // smoothly interpolated, never an instant jump.
    React.useEffect(() => {
      if (!controlled || !animated) return;
      const reduced = prefersReducedMotion();
      if (status === "loading") {
        if (typeof progress === "number") {
          const target = Math.min(1, Math.max(0, progress / 100));
          if (reduced) setProgress(target, performance.now());
          else driveTo(target, PROGRESS_MS);
        } else if (motion.current.raf === 0) {
          if (reduced) setProgress(P_CAP, performance.now());
          else startIndeterminate();
        }
      } else if (status === "success") {
        if (reduced) setProgress(1, performance.now());
        else driveTo(1, transitionDuration);
      } else if (status === "error") {
        stopRaf();
      } else {
        stopRaf();
        setProgress(0, performance.now());
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlled, animated, status, progress]);

    // Uncontrolled + numeric progress: the click's promise still decides
    // done/error, but the fill follows the caller's real progress.
    React.useEffect(() => {
      if (controlled || !animated || phase !== "pending" || typeof progress !== "number") return;
      const target = Math.min(1, Math.max(0, progress / 100));
      if (prefersReducedMotion()) setProgress(target, performance.now());
      else driveTo(target, PROGRESS_MS);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlled, animated, phase, progress]);

    // Fires the shockwave exactly once per transition into "done" -- never
    // on every render while it stays done, never looping.
    React.useEffect(() => {
      if (prevPhase.current !== "done" && effectivePhase === "done") setShockwaveKey(k => k + 1);
      prevPhase.current = effectivePhase;
    }, [effectivePhase]);

    // Uncontrolled mode: snap the fill to full (success) or empty (error),
    // hold the done/error label for doneDuration, then revert to idle.
    const settle = (ok: boolean) => {
      if (!mounted.current) return;
      const show = () => {
        if (!mounted.current) return;
        setPhase(ok ? "done" : "error");
        clearTimeout(revertTimer.current);
        revertTimer.current = window.setTimeout(() => {
          if (!mounted.current) return;
          setPhase("idle");
          if (animated) setProgress(0, performance.now());
        }, doneDuration);
      };
      if (!animated || prefersReducedMotion()) {
        if (animated) setProgress(ok ? 1 : 0, performance.now());
        show();
      } else {
        driveTo(ok ? 1 : 0, transitionDuration, show);
      }
    };

    const run = () => {
      if (controlled) {
        void onClick?.();
        return;
      }
      const result = onClick?.();
      // A click handler that returns nothing (open a dialog, navigate) gets
      // normal-button behavior -- no loading state, no false "done" flash.
      if (!isThenable(result)) return;
      setPhase("pending");
      if (animated) {
        if (prefersReducedMotion()) setProgress(P_CAP, performance.now());
        // A numeric progress means the caller knows the real progress -- the
        // effect below drives the fill from it instead.
        else if (typeof progress !== "number") startIndeterminate();
      }
      result.then(() => settle(true), () => settle(false));
    };

    const handleClick = () => {
      if (controlled ? status === "loading" : phase !== "idle") return;
      // Awaited before onClick runs, so the button never looks busy while the
      // user is still reading the card, and Cancel leaves it untouched.
      if (confirmRequest) {
        void requestConfirm(confirmRequest).then(ok => { if (ok) run(); });
        return;
      }
      run();
    };

    const setRefs = (node: HTMLButtonElement | null) => {
      buttonRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    };

    const fillTone = cn("bg-[var(--ab-accent)]", background === "variant" && VARIANT_FILL, fillClassName);
    // The completed/error fill is always a flat, fully solid rectangle --
    // never the wavy clip-path, however it happened to be mid-wobble when the
    // drive animation stopped.
    const fillLayerClass =
      effectivePhase === "done" ? cn("[clip-path:none]", fillTone)
      : effectivePhase === "error" ? cn("[clip-path:none]", ERROR_TONE)
      : cn("async-btn-fill", FILL_GLINT, fillTone);

    const showShockwave = animated && shockwave && completionIntensity !== "none" && shockwaveKey > 0;
    // Without a loadingText (and in animated mode, where there's no spinner),
    // the pending label would be identical to the idle one -- reuse it
    // instead of rendering the label a fourth time.
    const hasPendingSlot = loadingText !== undefined || !animated;
    const idleActive = effectivePhase === "idle" || (effectivePhase === "pending" && !hasPendingSlot);

    return (
      <button
        ref={setRefs}
        type="button"
        disabled={disabled || effectivePhase === "pending"}
        aria-busy={effectivePhase === "pending" || undefined}
        onClick={handleClick}
        className={cn(
          buttonVariants({ variant, size }),
          accentVariants({ variant: variant ?? "default", background }),
          radiusVariants({ radius }),
          borderVariants({ border }),
          hoverBorderVariants({ hoverBorder }),
          animated && [
            // The semantic variant color only shows once the fill wipes
            // across -- at rest the button sits on this neutral tone.
            "relative",
            backgroundVariants({ background }),
            intensityVariants({ completionIntensity }),
            // Dark label text on the light background would sit on the solid
            // fill once complete -- switch to white for contrast.
            background === "light" && (effectivePhase === "done" || effectivePhase === "error") && "text-white",
            effectivePhase === "pending" && "async-btn-progress-glow",
            effectivePhase === "done" && "async-btn-glow-pulse",
            // The button is functionally disabled while pending (to block
            // re-clicks), but buttonVariants' disabled:opacity-50 is meant for
            // a genuinely inert button -- it washed the whole animation out
            // into a flat gray. Only cancel it for the "busy" case; a
            // caller-disabled idle button still fades.
            effectivePhase === "pending" && "disabled:opacity-100",
          ],
          // Caller's className wins over the defaults above (one-off colors,
          // padding, width, etc.) -- but not over the plain-mode done/error
          // flash below, or that flash would be invisible.
          className,
          !animated && effectivePhase === "done" && SIMPLE_DONE_TONE,
          !animated && effectivePhase === "error" && SIMPLE_ERROR_TONE,
          "transition-[color,background-color,transform] active:scale-[0.97]"
        )}
        {...props}
      >
        {animated && (
          // Clips the fill to the button's rounded shape. The button itself
          // doesn't clip (no overflow-hidden), so the shockwave below can
          // still spread outside it -- which is what lets this render as a
          // single <button> with no wrapper, so w-full / flex-1 work.
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]">
            <span className={cn("absolute inset-0", fillLayerClass)} />
            {effectivePhase === "pending" && (
              // Darkens progressively as --ab-p rises, clipped to the same
              // wavy edge as the fill.
              <span className="async-btn-fill async-btn-deepen absolute inset-0" />
            )}
          </span>
        )}
        {showShockwave && (
          // Before the label in DOM order, so the ripple paints under the
          // text; unmounted once its one-shot animation finishes.
          <span
            key={shockwaveKey}
            aria-hidden="true"
            onAnimationEnd={() => setShockwaveKey(0)}
            className={cn("async-btn-shockwave pointer-events-none absolute inset-0 rounded-[inherit]", fillTone)}
          />
        )}
        <span className={cn(animated && "relative", "grid")}>
          <span aria-hidden={!idleActive} className={labelSlot(idleActive)}>
            {children}
          </span>
          {hasPendingSlot && (
            <span aria-hidden={effectivePhase !== "pending"} className={labelSlot(effectivePhase === "pending")}>
              {!animated && <Loader2 className="h-4 w-4 animate-spin" />}
              {loadingText ?? children}
            </span>
          )}
          <span aria-hidden={effectivePhase !== "done"} className={labelSlot(effectivePhase === "done")}>
            {doneIcon ?? <Check className="h-4 w-4" />}
            {doneLabel ?? children}
          </span>
          <span aria-hidden={effectivePhase !== "error"} className={labelSlot(effectivePhase === "error")}>
            <X className="h-4 w-4" />
            {errorLabel ?? children}
          </span>
        </span>
      </button>
    );
  }
);
AsyncButton.displayName = "AsyncButton";

export { AsyncButton };
